/**
 * ColoringQuiz Student Renderer
 * Student-style UI with bright colors, animations, and large touch targets.
 * Uses useColoringQuiz headless hook for logic.
 */

import { useMemo } from 'react'
import { cn } from '../../../lib/utils'
import { KatexText, SubmitButton, QuizSolveDescription, QuizMedia, QuizTypeBadge, QuizResultToggle, useQuizStyleVariant, type QuizResultToggleValue } from '../shared'
import type { QuizSolveResult } from '../../../types/legacyQuiz'
import type { ColoringQuizData } from '../hooks'
import { formatText } from '../../../utils/quiz-parser'
import { useColoringQuiz } from '../hooks'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'

interface ColoringStudentProps {
  quizId: string | number
  quiz: ColoringQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
  /** true면 결과 토글은 유지하되 풀이/해설 UI만 숨긴다. */
  hideSolveDescription?: boolean
  /** G4 — true면 유형별 노출표에 따라 발문/내용만 흐리고 이미지는 그대로 둔다. */
  dimChoiceText?: boolean
}

export function ColoringStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false,
}: ColoringStudentProps) {
  const { t } = useTranslation('quiz')
  const styleVariant = useQuizStyleVariant()

  const {
    answers,
    isSolved,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    digits,
    inverted,
    hasImages,
    correctColorCount,
    itemsPerRow,
    rowIndices,
    coloredCount,
    handleToggle,
    handleSubmit,
    getImageSrc,
    getIsColored,
  } = useColoringQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  // Feedback text computed with i18n
  const feedbackText = useMemo(() => {
    if (!isSolved) return ''

    if (shouldShowCorrectAnswers && correctColorCount !== null) {
      const count = inverted ? (digits - correctColorCount) : correctColorCount
      return inverted
        ? t('types.coloring.correctAnswerErase', { count })
        : t('types.coloring.correctAnswerColor', { count })
    }

    const count = inverted
      ? answers.filter(a => a === 0).length
      : coloredCount
    return inverted
      ? t('types.coloring.erased', { count })
      : t('types.coloring.colored', { count })
  }, [isSolved, shouldShowCorrectAnswers, correctColorCount, inverted, digits, answers, coloredCount, t])

  return (
    <div className="space-y-6">
      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-2"
      >
        <QuizTypeBadge
          quizId={quizId}
          typeLabel={t('types.coloring.label')}
          difficultyLevel={difficultyLevel}
          variant="student"
        />
      </motion.div>

      {/* Title + Problem + Media */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className={cn(
          styleVariant !== 'lc' && 'p-6 bg-linear-to-br to-gray-50 rounded-2xl from-slate-50',
          styleVariant !== 'lc' && 'border border-gray-100 shadow-2xs',
          'text-2xl font-medium leading-relaxed text-gray-900'
        )}
      >
        <span data-quiz-title-text className={cn(dimChoiceText && 'opacity-[0.05]')}>
          <KatexText text={quiz.title} />
        </span>
        {quiz.problem && (() => {
          const cleanProblem = quiz.problem.replace(/\{\{\}\}/g, '').trim()
          if (!cleanProblem) return null
          return (
            <div data-quiz-problem-text className={cn('mt-4 text-xl', dimChoiceText && 'opacity-[0.05]')}>
              {formatText(cleanProblem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
            </div>
          )
        })()}
        <QuizMedia image={quiz.image} imageMobile={quiz.imageMobile} desmos={quiz.desmos} vars={quiz.vars} />
      </motion.div>

      {/* Coloring grid - student style with animations */}
      <div className="flex flex-col items-center gap-2 py-4">
        {rowIndices.map((rowItems, rowIndex) => (
          <div key={rowIndex} className="flex items-center justify-center gap-2">
            {rowItems.map((itemIndex) => {
              const isColored = getIsColored(itemIndex)

              return (
                <motion.button
                  key={itemIndex}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: itemIndex * 0.03 }}
                  whileHover={!isSolved && !isViewSolved ? { scale: 1.1 } : undefined}
                  whileTap={!isSolved && !isViewSolved ? { scale: 0.95 } : undefined}
                  onClick={() => handleToggle(itemIndex)}
                  disabled={isSolved || isViewSolved}
                  className={cn(
                    'relative cursor-pointer select-none',
                    (isSolved || isViewSolved) && 'pointer-events-none'
                  )}
                >
                  {hasImages ? (
                    <img
                      src={getImageSrc(itemIndex)}
                      alt={`item-${itemIndex}`}
                      className="w-auto h-auto hover:opacity-50 transition-opacity"
                      style={{ maxWidth: `${Math.min(80, Math.floor(800 / itemsPerRow))}px` }}
                      draggable={false}
                    />
                  ) : (
                    <div className={cn(
                      'w-16 h-16 rounded-xl border-2 transition-all',
                      isColored
                        ? 'bg-lc-lightblue-900 border-lc-lightblue-dark'
                        : 'bg-gray-200 border-gray-300',
                      !isSolved && !isViewSolved && 'hover:scale-105'
                    )} />
                  )}
                </motion.button>
              )
            })}
          </div>
        ))}
      </div>

      {/* Colored count feedback bar (student colors) */}
      {isSolved && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={cn(
            'mt-6 rounded-2xl text-[20px] py-2 px-6 font-medium text-center relative',
            shouldShowCorrectAnswers || solveResult?.correct
              ? 'bg-lc-mint-200 text-lc-mint-dark'
              : 'bg-lc-red-200 text-lc-red-900'
          )}
        >
          {feedbackText}
        </motion.div>
      )}

      {/* Submit button */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={false}
          label={submitLabel}
        />
      )}

      {/* 풀이 결과 토글 (정답과 풀이 / 내가 쓴 답) */}
      {isSolved && !hideExplanation && (
        <QuizResultToggle
          value={isShowingCorrectAnswers ? 'correct-and-explanation' : 'my-answer'}
          onChange={(next: QuizResultToggleValue) => setIsShowingCorrectAnswers(next === 'correct-and-explanation')}
        />
      )}

      {/* 해설은 '정답과 풀이' 일 때만 노출 */}
      {isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={false}
        />
      )}
    </div>
  )
}
